import { useEffect, useState } from "react";
import moment from "moment-timezone";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/components/ui/use-toast";
import {
  CallTranscriptionCall,
  CallTranscriptionSummary,
  StagedTodo,
  useApproveTodo,
  useListCallTranscriptions,
} from "@/lib/api/customer";
import { CallParties, shouldShowCallCustomer } from "./CallContextLine";
import { CallTranscriptsDialog, callDisplayTime, failureLabel } from "./CallTranscriptsDialog";
import { HideTodoDialog } from "./HideTodoDialog";
import { ManageTodoDialog } from "./ManageTodoDialog";
import { StagedTodoCard } from "./StagedTodoCard";
import { TodoTranscriptDialog } from "./TodoTranscript";

const PAGE_SIZE = 25;
const API_DATE_FORMAT = "YYYY-MM-DD";

interface AllTranscriptionsListProps {
  customerIds: string[];
  traderIds: string[];
  startDate: Date | undefined;
  endDate: Date | undefined;
  hasFilters: boolean;
  onLoadingChange: (isLoading: boolean) => void;
  page: number;
  onPageChange: (page: number) => void;
}

/** Every recorded call with a transcription, whether or not it produced Todos. */
export function AllTranscriptionsList({
  customerIds,
  traderIds,
  startDate,
  endDate,
  hasFilters,
  onLoadingChange,
  page,
  onPageChange,
}: AllTranscriptionsListProps) {
  const { toast } = useToast();
  const approveTodo = useApproveTodo();

  const [callToView, setCallToView] = useState<CallTranscriptionCall | null>(null);
  const [todoToFlag, setTodoToFlag] = useState<StagedTodo | null>(null);
  const [todoToHide, setTodoToHide] = useState<StagedTodo | null>(null);
  const [todoToReview, setTodoToReview] = useState<StagedTodo | null>(null);
  const [approvingIds, setApprovingIds] = useState<Set<string>>(new Set());

  const callsQuery = useListCallTranscriptions({
    customer_ids: customerIds,
    trader_ids: traderIds,
    start_date: startDate ? moment(startDate).format(API_DATE_FORMAT) : undefined,
    end_date: endDate ? moment(endDate).format(API_DATE_FORMAT) : undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  // The page header shows "Loading..." while this list loads or refetches.
  const isFetching = callsQuery.isFetching;
  useEffect(() => {
    onLoadingChange(isFetching);
    return () => onLoadingChange(false);
  }, [isFetching, onLoadingChange]);

  const callsResult = callsQuery.data;
  // `keepPreviousData` keeps the old page in `data` while the next one loads. Show nothing
  // instead, so the list doesn't change under the user after the scroll to the top.
  const isPageLoading = callsQuery.isPreviousData;
  const calls: CallTranscriptionCall[] = callsResult?.success && !isPageLoading ? callsResult.data.data : [];
  const pagination = callsResult?.success ? callsResult.data.pagination : null;
  const loadError = callsQuery.isError
    ? "An unexpected error occurred while loading call transcriptions."
    : callsResult && !callsResult.success
      ? callsResult.message || "Call transcriptions could not be loaded."
      : null;

  // Approving can't shrink this list, but a refetch can leave `page` past the end. Step back from an empty page.
  // `currentPage === page` ignores the previous page's data kept while a new page loads.
  const isPastEnd = !!pagination && !isPageLoading && pagination.currentPage === page && page > 1 && calls.length === 0;
  useEffect(() => {
    if (isPastEnd) {
      onPageChange(page - 1);
    }
  }, [isPastEnd, page, onPageChange]);

  const handleApprove = async (todo: StagedTodo) => {
    if (approvingIds.has(todo.id)) return;

    setApprovingIds((prev: Set<string>) => new Set(prev).add(todo.id));

    try {
      const result = await approveTodo({ todo_id: todo.id });

      if (!result.success) {
        toast({
          title: "Unable to approve Todo",
          description: result.message,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Todo approved",
        description: "The Todo now appears in the To-Dos list.",
      });
    } catch {
      toast({
        title: "Unable to approve Todo",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setApprovingIds((prev: Set<string>) => {
        const next = new Set(prev);
        next.delete(todo.id);
        return next;
      });
    }
  };

  return (
    <>
      {!callsQuery.isLoading && loadError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          <p>{loadError}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => callsQuery.refetch()}
            disabled={callsQuery.isFetching}
          >
            {callsQuery.isFetching ? "Retrying..." : "Try Again"}
          </Button>
        </div>
      )}

      {!callsQuery.isLoading && !isPageLoading && !loadError && calls.length === 0 && (
        <div className="py-8 text-center text-sm text-muted-foreground">
          {hasFilters ? "No calls match these filters." : "No transcribed calls yet."}
        </div>
      )}

      {!callsQuery.isLoading && !loadError && calls.length > 0 && (
        <div className="space-y-2">
          {calls.map((call: CallTranscriptionCall) => (
            <CallRow
              key={call.id}
              call={call}
              approvingIds={approvingIds}
              onViewTranscripts={() => setCallToView(call)}
              onReviewTodo={setTodoToReview}
              onFlagTodo={setTodoToFlag}
              onHide={setTodoToHide}
              onApprove={handleApprove}
            />
          ))}
        </div>
      )}

      {pagination && !isPageLoading && (calls.length > 0 || page > 1) && (
        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onPageChange(Math.max(page - 1, 1))}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={!pagination.hasNextPage}
          >
            Next
          </Button>
        </div>
      )}

      <CallTranscriptsDialog call={callToView} onClose={() => setCallToView(null)} />
      <ManageTodoDialog todo={todoToFlag} onClose={() => setTodoToFlag(null)} showReviewActions />
      <HideTodoDialog todo={todoToHide} onClose={() => setTodoToHide(null)} />
      <TodoTranscriptDialog todo={todoToReview} onClose={() => setTodoToReview(null)} showReviewActions />
    </>
  );
}

interface CallRowProps {
  call: CallTranscriptionCall;
  approvingIds: Set<string>;
  onViewTranscripts: () => void;
  onReviewTodo: (todo: StagedTodo) => void;
  onFlagTodo: (todo: StagedTodo) => void;
  onHide: (todo: StagedTodo) => void;
  onApprove: (todo: StagedTodo) => void;
}

function CallRow({
  call,
  approvingIds,
  onViewTranscripts,
  onReviewTodo,
  onFlagTodo,
  onHide,
  onApprove,
}: CallRowProps) {
  const [isTodosOpen, setIsTodosOpen] = useState(false);
  const hasAnyTranscript = call.transcriptions.some((transcription: CallTranscriptionSummary) => transcription.has_transcript);
  const failures = Array.from(
    new Set(call.transcriptions.flatMap((transcription: CallTranscriptionSummary) => transcription.failures)),
  );
  const needsReviewCount = call.todos.filter((todo: StagedTodo) => !todo.approved_date && !todo.hidden_date).length;

  return (
    <Card data-testid={`call-transcription-${call.id}`}>
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex w-full flex-wrap items-center gap-3">
          {shouldShowCallCustomer(call.customer, call.from, call.to) && (
            <span className="text-sm font-semibold text-red-600">
              {call.customer?.long_name || "Unknown Customer"}
            </span>
          )}
          {(call.from || call.to) && (
            <span className="text-sm text-muted-foreground">
              <CallParties from={call.from} to={call.to} />
            </span>
          )}
          <time
            className="text-sm text-muted-foreground"
            dateTime={call.call_started_at || call.created_at}
          >
            {callDisplayTime(call)}
          </time>
          {failures.map((code: string) => (
            <Badge
              key={code}
              variant="destructive"
              data-testid={`call-transcription-failure-${call.id}`}
            >
              {failureLabel(code)}
            </Badge>
          ))}
          <div className="ml-auto">
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid={`call-transcription-view-${call.id}`}
              onClick={onViewTranscripts}
              disabled={call.transcriptions.length === 0}
              title={hasAnyTranscript ? undefined : "No transcript was produced for this call"}
            >
              View Transcripts
            </Button>
          </div>
        </div>

        {call.todos.length > 0 && (
          <Collapsible open={isTodosOpen} onOpenChange={setIsTodosOpen} className="rounded-md border">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                data-testid={`call-transcription-todos-toggle-${call.id}`}
                className="flex w-full items-center gap-2 p-3 text-left text-sm font-medium hover:bg-muted/40"
              >
                {isTodosOpen ? (
                  <ChevronDown className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                )}
                Todos ({call.todos.length})
                {needsReviewCount > 0 && (
                  <span className="text-xs font-normal text-muted-foreground">{needsReviewCount} need review</span>
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2 border-t p-3">
              {call.todos.map((todo: StagedTodo) => (
                <StagedTodoCard
                  key={todo.id}
                  todo={todo}
                  showStatus
                  hideCallParties
                  isApproving={approvingIds.has(todo.id)}
                  onReview={() => onReviewTodo(todo)}
                  onFlag={() => onFlagTodo(todo)}
                  onHide={() => onHide(todo)}
                  onApprove={() => onApprove(todo)}
                />
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}
